import { ApiPublicConnectionFull } from '@nangohq/types';
import { getAccessToken } from '../../auth';
import { envConfig } from '../../config';

export const editorRuntimeServiceBaseUrl =
	`${envConfig.API_BASE_URL}/editor-runtime` ||
	'http://localhost:3010';

type File = {
	[key: string]: any;
};

export async function postConnectSession(
	integration: string,
	projectId: string
): Promise<any> {
	const res = await fetch(
		`${editorRuntimeServiceBaseUrl}/v1/external-integrations/connect-session/${projectId}`,
		{
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
				Authorization: `Bearer ${await getAccessToken()}`,
			},
			body: JSON.stringify({ integration }),
		}
	);
	if (res.status !== 200) {
		throw new Error();
	}

	const json = (await res.json()) as any;
	return json;
}

export async function listIntegrations(): Promise<any> {
	const res = await fetch(`${editorRuntimeServiceBaseUrl}/integrations`, {
		method: 'GET',
		headers: {
			Authorization: `Bearer ${await getAccessToken()}`,
		},
	});
	if (res.status !== 200) {
		throw new Error();
	}

	const json = (await res.json()) as any;
	return json;
}

export async function listConnections(): Promise<any> {
	const res = await fetch(`${editorRuntimeServiceBaseUrl}/v1/external-integrations/connections`, {
		method: 'GET',
		headers: {
			Authorization: `Bearer ${await getAccessToken()}`,
		},
	});
	if (res.status !== 200) {
		throw new Error();
	}

	const json = (await res.json()) as any;
	return json;
}

export async function getNangoCredentials(integrationId: string): Promise<any> {
	const res = await fetch(
		`${editorRuntimeServiceBaseUrl}/nango-credentials?integrationId=${integrationId}`
	);
	if (res.status !== 200) {
		throw new Error('Failed to get Nango credentials');
	}
	return res.json();
}

export async function setConnectionMetadata(
	integrationId: string,
	metadata: Record<string, any>
): Promise<void> {
	const res = await fetch(
		`${editorRuntimeServiceBaseUrl}/v1/external-integrations/set-connection-metadata`,
		{
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
				Authorization: `Bearer ${await getAccessToken()}`,
			},
			body: JSON.stringify({ integrationId, metadata }),
		}
	);
	if (res.status !== 200) {
		throw new Error('Failed to set connection metadata');
	}
}

export async function getFiles(): Promise<File[]> {
	const res = await fetch(`${editorRuntimeServiceBaseUrl}/get-files`);
	if (res.status !== 200) {
		throw new Error('Failed to get files');
	}
	const json: { files: File[] } = await res.json();
	return json.files;
}

export async function downloadFile(fileId: string): Promise<Blob> {
	const res = await fetch(`${editorRuntimeServiceBaseUrl}/download/${fileId}`, {
		method: 'GET',
	});

	if (!res.ok) {
		throw new Error('Failed to download file');
	}

	return res.blob();
}

export async function deleteConnection(integration: string): Promise<void> {
	const res = await fetch(
		`${editorRuntimeServiceBaseUrl}/v1/external-integrations/connections/${integration}`,
		{
			headers: {
				Authorization: `Bearer ${await getAccessToken()}`,
			},
			method: 'DELETE',
		}
	);
	if (res.status !== 200) {
		throw new Error('Failed to delete connection');
	}
}

export async function getProjectCredentials(
	projectId: string,
	connectionIds?: string[]
): Promise<{ credentials: ApiPublicConnectionFull[] }> {
	const res = await fetch(
		`${editorRuntimeServiceBaseUrl}/v1/external-integrations/credentials/${projectId}${
			!!connectionIds ? `?connectionIds=${connectionIds.join(',')}` : ''
		}`,
		{
			method: 'GET',
			headers: {
				Authorization: `Bearer ${await getAccessToken()}`,
			},
		}
	);

	if (res.status !== 200) {
		throw new Error('Failed to get credentials');
	}

	return res.json();
}

export async function getCredentials(
	projectId: string,
	integrationId: string,
	connectionId: string
): Promise<{ credentials: ApiPublicConnectionFull[] }> {
	const res = await fetch(
		`${editorRuntimeServiceBaseUrl}/v1/external-integrations/credentials/${projectId}/${integrationId}/${connectionId}`,
		{
			method: 'GET',
			headers: {
				Authorization: `Bearer ${await getAccessToken()}`,
			},
		}
	);

	if (res.status !== 200) {
		throw new Error('Failed to get credentials');
	}

	return res.json();
}
